/**
 * Public quote view (Phase 3.4) — no auth required.
 *
 * Customers land here from the email or WhatsApp deep-link they received
 * after the admin sent them a quote. They can:
 *   • View the quote line items + grand total
 *   • Reject the quote (one click)
 *   • Proceed to register, with their name/email/phone pre-filled
 *     into /cabinet/register
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowRight, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SeoMeta from '@/components/SeoMeta';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_LABEL = {
  quoted:   { en: 'Awaiting your decision', ka: 'ელოდება შენს გადაწყვეტილებას' },
  accepted: { en: 'Accepted',  ka: 'მიღებული' },
  rejected: { en: 'Rejected',  ka: 'უარყოფილი' },
  closed:   { en: 'Closed',    ka: 'დახურული' },
};

export default function QuoteViewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // 'reject' | 'accept' | null
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // 'rejected' | 'accepted'

  useEffect(() => {
    document.title = 'SmartPaw — Quote';
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/quote/${token}`);
        if (!res.ok) throw new Error('Quote not found');
        const data = await res.json();
        if (!cancelled) setQuote(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Quote not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const lang = (typeof window !== 'undefined'
    && (localStorage.getItem('sp-lang') || 'ka')) || 'ka';
  const L = (en, ka) => (lang === 'ka' ? ka : en);

  const onReject = async () => {
    setActing('reject');
    try {
      await fetch(`${API}/api/quote/${token}/reject`, { method: 'POST' });
      setDone('rejected');
    } catch {
      setError(L('Could not record your decision', 'შენი გადაწყვეტილების შენახვა ვერ მოხერხდა'));
    } finally {
      setActing(null);
    }
  };

  const onAccept = async () => {
    setActing('accept');
    try {
      const res = await fetch(`${API}/api/quote/${token}/accept`, { method: 'POST' });
      const data = await res.json();
      // Pass the prefill to the register page through sessionStorage so the
      // wizard can read it on mount. Keeps the URL clean.
      if (data?.prefill) {
        sessionStorage.setItem('sp_register_prefill', JSON.stringify(data.prefill));
      }
      navigate('/cabinet/register');
    } catch {
      setError(L('Could not proceed', 'გაგრძელება ვერ მოხერხდა'));
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="h-6 w-6 animate-spin text-[#0A4D8C]" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF7] px-5">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="font-display text-xl">
              {L('Quote not found', 'შეთავაზება ვერ მოიძებნა')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-[#465B70]">
            <p>{L(
              'The link may have expired or the request was already closed. If you think this is a mistake, please reply to your email or WhatsApp message.',
              'ბმული შესაძლოა ვადაგასული იყოს ან მოთხოვნა უკვე დახურულია. თუ ფიქრობთ, რომ ეს შეცდომაა, გვიპასუხეთ ელფოსტაზე ან WhatsApp-ში.'
            )}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isFinal = ['accepted', 'rejected', 'closed'].includes(quote.status) || done;
  const finalLabel = done || quote.status;
  const cur = quote.currency || 'GEL';

  return (
    <>
      <SeoMeta
        title={`SmartPaw — ${L('Your quote', 'შენი შეთავაზება')}`}
        description={L('Review your SmartPaw quote', 'გადახედე SmartPaw-ის შეთავაზებას')}
      />
      <div className="min-h-screen bg-[#FDFBF7] py-12 md:py-20 px-5">
        <div className="mx-auto max-w-2xl" data-testid="quote-view-page">
          <div className="mb-7 flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0A4D8C] text-white">
              <FileText size={20} />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#F25C05]">
                {L('SmartPaw quote', 'SmartPaw შეთავაზება')}
              </p>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-[#05223D] tracking-tight">
                {L(`Hi ${quote.name || 'there'}`, `გამარჯობა ${quote.name || ''}`)}
              </h1>
            </div>
          </div>

          <Card className="bg-white border-[#0A4D8C]/10">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="font-display text-xl">
                  {L('Your offer', 'შენი შეთავაზება')}
                </CardTitle>
                <span
                  data-testid={`quote-status-${finalLabel}`}
                  className={`rounded-full px-3 py-1 text-xs font-bold ${
                    finalLabel === 'accepted'
                      ? 'bg-[#A9B89A]/40 text-[#05223D]'
                      : finalLabel === 'rejected'
                      ? 'bg-[#9b1c1c]/15 text-[#9b1c1c]'
                      : 'bg-[#EAC490]/60 text-[#05223D]'
                  }`}
                >
                  {STATUS_LABEL[finalLabel]?.[lang] || finalLabel}
                </span>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <ul className="divide-y divide-[#0A4D8C]/8 -mx-2" data-testid="quote-lines">
                {(quote.lines || []).map((l, i) => (
                  <li key={i} className="px-2 py-3 flex items-start justify-between gap-3" data-testid={`quote-line-${i}`}>
                    <div className="min-w-0">
                      <p className="font-bold text-[#05223D] truncate">{l.product_name}</p>
                      <p className="text-xs text-[#465B70]">{l.quantity} {l.unit} · {l.unit_price?.toFixed(2)} {cur} {L('per unit', 'ერთეული')}</p>
                      {l.note && <p className="text-xs text-[#465B70] italic mt-0.5">{l.note}</p>}
                    </div>
                    <div className="text-right tabular-nums font-bold text-[#05223D] shrink-0">
                      {l.line_total?.toFixed(2)} {cur}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-4 pt-4 border-t border-dashed border-[#0A4D8C]/15 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#465B70]">{L('Subtotal', 'ქვეჯამური')}</span>
                  <span className="tabular-nums font-bold">{quote.subtotal?.toFixed(2)} {cur}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#465B70]">{L('Delivery', 'მიწოდება')}</span>
                  <span className="tabular-nums font-bold">
                    {quote.delivery_fee > 0 ? `${quote.delivery_fee.toFixed(2)} ${cur}` : L('Free', 'უფასო')}
                  </span>
                </div>
                <div className="flex justify-between text-lg pt-2 border-t border-[#0A4D8C]/10">
                  <span className="font-bold text-[#05223D]">{L('Grand total', 'საერთო ჯამი')}</span>
                  <span className="tabular-nums font-extrabold text-[#F25C05]" data-testid="quote-grand-total">
                    {quote.total?.toFixed(2)} {cur}
                  </span>
                </div>
              </div>

              {quote.admin_note && (
                <div className="mt-5 rounded-xl bg-[#F5F2EB] p-3 text-sm text-[#465B70]" data-testid="quote-admin-note">
                  <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70] mb-1">
                    {L('Note from SmartPaw', 'SmartPaw-ის შენიშვნა')}
                  </p>
                  <p className="whitespace-pre-wrap">{quote.admin_note}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {!isFinal ? (
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="quote-actions">
              <Button
                onClick={onReject}
                variant="outline"
                className="rounded-xl border-[#9b1c1c]/30 text-[#9b1c1c] hover:bg-[#9b1c1c]/8"
                disabled={!!acting}
                data-testid="quote-reject-button"
              >
                {acting === 'reject' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <XCircle className="mr-1.5 h-4 w-4" />
                {L('Reject', 'უარყოფა')}
              </Button>
              <Button
                onClick={onAccept}
                className="rounded-xl bg-[#F25C05] hover:bg-[#d44e02]"
                disabled={!!acting}
                data-testid="quote-accept-button"
              >
                {acting === 'accept' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {L('Proceed to Registration', 'რეგისტრაცია')}
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="mt-7 rounded-2xl border border-dashed border-[#0A4D8C]/20 bg-white px-5 py-4 text-sm text-[#465B70]" data-testid="quote-final-state">
              {finalLabel === 'accepted' ? (
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#0A4D8C]" />
                  {L('You\u2019ve accepted this quote. We\u2019ll get in touch shortly!', 'შენ მიიღე ეს შეთავაზება. მალე დაგიკავშირდებით!')}
                </p>
              ) : (
                <p className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-[#9b1c1c]" />
                  {L('You rejected this quote. If you\u2019d like a different offer, just reply to your email or WhatsApp.', 'შენ უარყავი ეს შეთავაზება. სხვა შეთავაზებისთვის გვიპასუხე ელფოსტაზე ან WhatsApp-ში.')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
